<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ContactConsentsAPI from 'dashboard/api/contactConsents';
import ContactsAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();

const records = ref([]);
const recordsSearch = ref('');
const isLoading = ref(true);
const isCreating = ref(false);
const cancellingId = ref(null);
const recoveringId = ref(null);
const kind = ref('opt_in');
const expiresInHours = ref(24);
const selectedContact = ref(null);
const contactSearch = ref('');
const contactOptions = ref([]);
const isSearchingContacts = ref(false);
const generatedLink = ref('');
const generatedConsentId = ref(null);
const deliveryMethod = ref('link');
const recipientEmail = ref('');
const phoneDialogRef = ref(null);
const isDelivering = ref(false);
const phoneForm = reactive({
  inboxId: '',
  phone: '',
  templateIndex: '',
});
const inboxes = useMapGetter('inboxes/getInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const validityOptions = [
  { value: 1, label: '1 hora' },
  { value: 24, label: '24 horas' },
  { value: 168, label: '7 dias' },
  { value: 720, label: '30 dias' },
];

const deliveryOptions = [
  { value: 'link', label: 'Somente gerar' },
  { value: 'email', label: 'Enviar por e-mail' },
  { value: 'phone', label: 'Enviar por telefone' },
];

const statusLabels = {
  pending: 'Aguardando',
  accepted: 'Opt-in aceito',
  revoked: 'Opt-out confirmado',
  expired: 'Expirado',
  cancelled: 'Cancelado',
};

const statusClasses = {
  pending: 'bg-n-amber-3 text-n-amber-11',
  accepted: 'bg-n-teal-3 text-n-teal-11',
  revoked: 'bg-n-ruby-3 text-n-ruby-11',
  expired: 'bg-n-alpha-2 text-n-slate-11',
  cancelled: 'bg-n-alpha-2 text-n-slate-11',
};

const selectedContactLabel = contact =>
  contact?.name || contact?.phone_number || contact?.email || '';

const filteredRecords = computed(() => {
  const query = recordsSearch.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return records.value;

  return records.value.filter(record =>
    [
      record.contact?.name,
      record.contact?.phone_number,
      record.contact?.email,
      record.kind === 'opt_in' ? 'opt-in' : 'opt-out',
      statusLabels[record.status],
    ].some(value =>
      String(value || '')
        .toLocaleLowerCase('pt-BR')
        .includes(query)
    )
  );
});

const goToContact = contactId => {
  if (!contactId) return;
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId },
  });
};

const canCreate = computed(() => {
  const hasContact =
    kind.value === 'opt_in' || Boolean(selectedContact.value?.id);
  const hasEmail =
    deliveryMethod.value !== 'email' ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.value);
  return hasContact && hasEmail;
});

const phoneInboxes = computed(() =>
  (inboxes.value || []).filter(inbox =>
    ['Channel::Whatsapp', 'Channel::TwilioSms', 'Channel::Sms'].includes(
      inbox.channel_type
    )
  )
);

const phoneTemplates = computed(() =>
  phoneForm.inboxId
    ? getFilteredWhatsAppTemplates.value(phoneForm.inboxId) || []
    : []
);

const selectedPhoneTemplate = computed(() =>
  phoneForm.templateIndex === ''
    ? null
    : phoneTemplates.value[Number(phoneForm.templateIndex)]
);

const formatDate = value =>
  value
    ? new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : '—';

const fetchRecords = async () => {
  isLoading.value = true;
  try {
    const { data } = await ContactConsentsAPI.get();
    records.value = data?.payload || [];
  } catch {
    useAlert('Não foi possível carregar os consentimentos.');
  } finally {
    isLoading.value = false;
  }
};

const searchContacts = debounce(async value => {
  const query = value.trim();
  if (query.length < 2) {
    contactOptions.value = [];
    return;
  }

  isSearchingContacts.value = true;
  try {
    const { data } = await ContactsAPI.search(
      encodeURIComponent(query),
      1,
      'name'
    );
    contactOptions.value = data?.payload || [];
  } catch {
    contactOptions.value = [];
  } finally {
    isSearchingContacts.value = false;
  }
}, 300);

const selectContact = contact => {
  selectedContact.value = contact;
  contactSearch.value = selectedContactLabel(contact);
  contactOptions.value = [];
};

function openPhoneDialog() {
  phoneForm.inboxId = '';
  phoneForm.phone = '';
  phoneForm.templateIndex = '';
  phoneDialogRef.value?.open();
}

const createLink = async () => {
  if (!canCreate.value || isCreating.value) return;

  isCreating.value = true;
  generatedLink.value = '';
  try {
    const { data } = await ContactConsentsAPI.create({
      kind: kind.value,
      expires_in_hours: expiresInHours.value,
      contact_id: selectedContact.value?.id,
      delivery_method: deliveryMethod.value,
      recipient_email:
        deliveryMethod.value === 'email' ? recipientEmail.value : undefined,
    });
    generatedLink.value = data?.consent?.public_url || '';
    generatedConsentId.value = data?.consent?.id || null;
    useAlert(
      deliveryMethod.value === 'email'
        ? 'Link gerado e enviado por e-mail.'
        : 'Link gerado com sucesso.'
    );
    await fetchRecords();
    if (deliveryMethod.value === 'phone') openPhoneDialog();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Não foi possível gerar o link.');
  } finally {
    isCreating.value = false;
  }
};

const recoverLink = async record => {
  recoveringId.value = record.id;
  try {
    const { data } = await ContactConsentsAPI.renew(record.id, {
      expires_in_hours: expiresInHours.value,
    });
    generatedLink.value = data?.consent?.public_url || '';
    generatedConsentId.value = data?.consent?.id || record.id;
    useAlert('Novo link seguro gerado. O link anterior foi invalidado.');
    await fetchRecords();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível recuperar o link.'
    );
  } finally {
    recoveringId.value = null;
  }
};

const formatPhone = value => {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
};

const templateLabel = template => template.name || template.friendly_name;
const findBodyComponent = template =>
  Array(template?.components).find(component => component.type === 'BODY');
const buildTemplateParams = template => {
  if (!template) return null;
  const body = findBodyComponent(template)?.text || template.body || '';
  const variable = body.match(/\{\{\s*([\w.]+)\s*\}\}/)?.[1];
  return {
    name: template.name || template.friendly_name,
    namespace: template.namespace,
    language: template.language || 'pt_BR',
    processed_params: variable
      ? { body: { [variable]: generatedLink.value } }
      : {},
  };
};

const handlePhoneInput = value => {
  phoneForm.phone = formatPhone(value);
};

const sendByPhone = async () => {
  if (
    !generatedConsentId.value ||
    !generatedLink.value ||
    !phoneForm.inboxId ||
    phoneForm.phone.replace(/\D/g, '').length < 10
  ) {
    useAlert('Selecione a caixa e informe um telefone válido.');
    return;
  }

  isDelivering.value = true;
  try {
    await ContactConsentsAPI.deliver(generatedConsentId.value, {
      delivery_method: 'phone',
      recipient_phone: phoneForm.phone,
      inbox_id: phoneForm.inboxId,
      public_url: generatedLink.value,
      template_params: buildTemplateParams(selectedPhoneTemplate.value),
    });
    useAlert('Link enviado para o telefone informado.');
    phoneDialogRef.value?.close();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Não foi possível enviar o link.');
  } finally {
    isDelivering.value = false;
  }
};

const copyLink = async () => {
  await copyTextToClipboard(generatedLink.value);
  useAlert('Link copiado.');
};

const cancelLink = async record => {
  cancellingId.value = record.id;
  try {
    await ContactConsentsAPI.cancel(record.id);
    useAlert('Link cancelado.');
    await fetchRecords();
  } catch {
    useAlert('Não foi possível cancelar o link.');
  } finally {
    cancellingId.value = null;
  }
};

watch(kind, value => {
  generatedLink.value = '';
  if (value === 'opt_in') {
    selectedContact.value = null;
    contactSearch.value = '';
    contactOptions.value = [];
  }
});

watch(contactSearch, value => {
  if (value !== selectedContactLabel(selectedContact.value)) {
    selectedContact.value = null;
  }
  if (kind.value === 'opt_out') searchContacts(value);
});

onMounted(() => {
  fetchRecords();
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="h-full overflow-y-auto bg-n-surface-1 px-6 py-6">
    <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <header
        class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
      >
        <div>
          <button
            type="button"
            class="mb-3 flex items-center gap-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
            @click="
              router.push({
                name: 'contacts_dashboard_index',
                params: { accountId: route.params.accountId },
                query: { page: 1 },
              })
            "
          >
            <span class="i-lucide-arrow-left size-4" />
            Voltar para contatos
          </button>
          <h1 class="mb-0 text-2xl font-semibold text-n-slate-12">
            Opt-in e opt-out
          </h1>
          <p class="mb-0 mt-2 max-w-2xl text-sm leading-6 text-n-slate-11">
            Gere links temporários para o cliente cadastrar seus dados, aceitar
            comunicações ou revogar um consentimento existente.
          </p>
        </div>
      </header>

      <section
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
      >
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-4">
            <div>
              <h2 class="mb-0 text-lg font-semibold text-n-slate-12">
                Gerar novo link
              </h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                Cada link é individual, expira automaticamente e só pode ser
                concluído uma vez.
              </p>
            </div>

            <fieldset class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label
                v-for="option in [
                  {
                    value: 'opt_in',
                    title: 'Opt-in',
                    description: 'Cadastrar cliente e registrar o aceite.',
                  },
                  {
                    value: 'opt_out',
                    title: 'Opt-out',
                    description: 'Revogar o consentimento de um cliente.',
                  },
                ]"
                :key="option.value"
                class="cursor-pointer rounded-xl border px-4 py-3 transition-colors"
                :class="
                  kind === option.value
                    ? 'border-n-brand bg-n-brand/8'
                    : 'border-n-weak hover:bg-n-alpha-2'
                "
              >
                <input
                  v-model="kind"
                  type="radio"
                  :value="option.value"
                  class="sr-only"
                />
                <span class="block text-sm font-semibold text-n-slate-12">{{
                  option.title
                }}</span>
                <span class="mt-0.5 block text-xs leading-5 text-n-slate-11">{{
                  option.description
                }}</span>
              </label>
            </fieldset>

            <div v-if="kind === 'opt_out'" class="relative">
              <Input
                v-model="contactSearch"
                label="Cliente"
                placeholder="Pesquise por nome, telefone ou e-mail"
              />
              <div
                v-if="contactOptions.length || isSearchingContacts"
                class="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-lg"
              >
                <p
                  v-if="isSearchingContacts"
                  class="mb-0 px-3 py-3 text-sm text-n-slate-11"
                >
                  Pesquisando...
                </p>
                <template v-else>
                  <button
                    v-for="contact in contactOptions"
                    :key="contact.id"
                    type="button"
                    class="flex w-full flex-col rounded-lg px-3 py-2 text-left hover:bg-n-alpha-2"
                    @click="selectContact(contact)"
                  >
                    <span class="text-sm font-medium text-n-slate-12">{{
                      contact.name
                    }}</span>
                    <span class="text-xs text-n-slate-11">{{
                      contact.phone_number || contact.email
                    }}</span>
                  </button>
                </template>
              </div>
            </div>
          </div>

          <aside
            class="grid grid-cols-1 items-end gap-3 border-t border-n-weak pt-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            <label class="flex flex-col gap-1.5 text-heading-3 text-n-slate-12">
              Validade do link
              <Select
                v-model="expiresInHours"
                :options="validityOptions"
                class="w-full [&>select]:w-full"
              />
            </label>
            <label class="flex flex-col gap-1.5 text-heading-3 text-n-slate-12">
              Ao gerar o link
              <Select
                v-model="deliveryMethod"
                :options="deliveryOptions"
                class="w-full [&>select]:w-full"
              />
            </label>
            <Input
              v-if="deliveryMethod === 'email'"
              v-model="recipientEmail"
              type="email"
              label="E-mail do destinatário"
              placeholder="cliente@exemplo.com"
              class="w-full"
            />
            <Button
              label="Gerar link seguro"
              icon="i-lucide-link"
              class="w-full lg:col-start-4"
              :is-loading="isCreating"
              :disabled="!canCreate"
              @click="createLink"
            />
          </aside>
        </div>

        <div
          v-if="generatedLink"
          class="mt-5 flex flex-col gap-3 rounded-xl border border-n-teal-7/50 bg-n-teal-3/30 p-4 sm:flex-row sm:items-center"
        >
          <div class="min-w-0 flex-1">
            <p class="mb-1 text-sm font-semibold text-n-slate-12">
              Link pronto para compartilhar
            </p>
            <p class="mb-0 truncate text-xs text-n-slate-11">
              {{ generatedLink }}
            </p>
          </div>
          <div class="flex gap-2">
            <Button
              label="Copiar"
              icon="i-lucide-copy"
              color="slate"
              variant="outline"
              @click="copyLink"
            />
            <a :href="generatedLink" target="_blank" rel="noopener noreferrer">
              <Button
                label="Abrir"
                icon="i-lucide-external-link"
                color="slate"
                variant="outline"
              />
            </a>
            <Button
              label="Enviar por telefone"
              icon="i-lucide-send"
              color="slate"
              variant="outline"
              @click="openPhoneDialog"
            />
          </div>
        </div>
      </section>

      <section class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm">
        <header
          class="flex flex-col gap-3 border-b border-n-weak px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <h2 class="mb-0 text-base font-semibold text-n-slate-12">
            Links gerados recentemente
          </h2>
          <Input
            v-model="recordsSearch"
            type="search"
            placeholder="Buscar por contato, status ou tipo"
            custom-input-class="sm:!w-80"
          />
        </header>
        <p
          v-if="isLoading"
          class="mb-0 px-5 py-12 text-center text-sm text-n-slate-11"
        >
          Carregando...
        </p>
        <p
          v-else-if="!records.length"
          class="mb-0 px-5 py-12 text-center text-sm text-n-slate-11"
        >
          Nenhum link gerado ainda.
        </p>
        <p
          v-else-if="!filteredRecords.length"
          class="mb-0 px-5 py-12 text-center text-sm text-n-slate-11"
        >
          Nenhum consentimento corresponde à busca.
        </p>
        <div v-else class="divide-y divide-n-weak">
          <article
            v-for="record in filteredRecords"
            :key="record.id"
            class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-medium text-n-slate-12">{{
                  record.kind === 'opt_in' ? 'Opt-in' : 'Opt-out'
                }}</span>
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="statusClasses[record.status]"
                >
                  {{ statusLabels[record.status] || record.status }}
                </span>
              </div>
              <p class="mb-0 mt-1 truncate text-sm text-n-slate-11">
                {{ record.contact?.name || 'Novo cliente' }} · gerado em
                {{ formatDate(record.created_at) }} · expira em
                {{ formatDate(record.expires_at) }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button
                v-if="record.contact?.id"
                label="Ir para contato"
                icon="i-lucide-user-round"
                color="slate"
                variant="outline"
                @click="goToContact(record.contact.id)"
              />
              <Button
                v-if="record.status === 'pending'"
                label="Recuperar link"
                icon="i-lucide-refresh-cw"
                color="slate"
                variant="outline"
                :is-loading="recoveringId === record.id"
                @click="recoverLink(record)"
              />
              <Button
                v-if="record.status === 'pending'"
                label="Cancelar"
                color="slate"
                variant="ghost"
                :is-loading="cancellingId === record.id"
                @click="cancelLink(record)"
              />
            </div>
          </article>
        </div>
      </section>
    </div>

    <Dialog
      ref="phoneDialogRef"
      width="md"
      title="Enviar link por telefone"
      description="Selecione a caixa de saída, informe o telefone e escolha um template quando necessário."
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div class="grid gap-4">
        <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
          Caixa de entrada
          <select
            v-model="phoneForm.inboxId"
            class="h-10 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            @change="phoneForm.templateIndex = ''"
          >
            <option value="">Selecione uma caixa</option>
            <option
              v-for="inbox in phoneInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name || inbox.channel_type }}
            </option>
          </select>
        </label>

        <p
          v-if="!phoneInboxes.length"
          class="mb-0 rounded-lg bg-n-amber-3 p-3 text-sm text-n-amber-11"
        >
          Nenhuma caixa de WhatsApp ou SMS está disponível.
        </p>

        <Input
          :model-value="phoneForm.phone"
          label="Telefone com DDD"
          placeholder="(11) 99999-9999"
          inputmode="tel"
          @update:model-value="handlePhoneInput"
        />

        <label
          v-if="phoneForm.inboxId && phoneTemplates.length"
          class="grid gap-1.5 text-sm font-medium text-n-slate-12"
        >
          Template de mensagem
          <select
            v-model="phoneForm.templateIndex"
            class="h-10 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">Mensagem simples</option>
            <option
              v-for="(template, index) in phoneTemplates"
              :key="template.id || template.name || index"
              :value="index"
            >
              {{ templateLabel(template) }}
            </option>
          </select>
        </label>

        <div class="flex justify-end gap-2 pt-2">
          <Button
            label="Cancelar"
            color="slate"
            variant="ghost"
            @click="phoneDialogRef?.close()"
          />
          <Button
            label="Enviar link"
            icon="i-lucide-send"
            :is-loading="isDelivering"
            :disabled="
              isDelivering ||
              !phoneForm.inboxId ||
              phoneForm.phone.replace(/\D/g, '').length < 10
            "
            @click="sendByPhone"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>
